// src/utils/constants.js
import { Logger } from './logger';

const logger = new Logger('constants');

// System date — single frontend source of truth.
// To go live: change this value. All other files import CURRENT_DATE from here.
export const CURRENT_DATE = new Date(2024, 7, 26); // Aug 26, 2024 local time — matches backend SYSTEM_DATE

/**
 * Format the date specifically for the header display.
 * Derived from CURRENT_DATE so changing that value above is enough.
 * @returns {string} Formatted date string (e.g., "August 27, 2024")
 */
export const getFormattedHeaderDate = () => {
  return CURRENT_DATE.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};


export const formatDate = (date, format = 'MMM D, YYYY') => {
 if (!date) return 'N/A';
 
 try {
   let dateObj;
   
   if (date instanceof Date) {
     dateObj = date;
   } else if (typeof date === 'string') {
     // Handle MM/DD/YYYY format
     if (date.includes('/')) {
       const [month, day, year] = date.split('/');
       dateObj = new Date(year, month - 1, day);
     } 
     // Handle ISO dates
     else if (date.includes('T')) {
       dateObj = new Date(date);
     } 
     // Handle YYYY-MM-DD format
     else if (date.includes('-')) {
       dateObj = new Date(date);
     }
     // Try direct parsing as fallback
     else {
       dateObj = new Date(date);
     }
   } else {
     dateObj = new Date(date);
   }
   
   // Check if valid date
   if (isNaN(dateObj.getTime())) {
     logger.warn('Invalid date:', date);
     return String(date);
   }
   
   // Format using Intl or other formatting library
   // This is a simple implementation - consider using a library like date-fns
   // for more robust formatting
   return new Intl.DateTimeFormat('en-US', {
     month: 'short',
     day: 'numeric',
     year: 'numeric'
   }).format(dateObj);
 } catch (error) {
   logger.error('Error formatting date:', error, 'for date:', date);
   return String(date); // Return the raw string if parsing fails
 }
};
/**
 * Format currency values consistently
 * @param {number|string} amount - Amount to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '$0';
  
  // If amount is already a string with a currency symbol, return it
  if (typeof amount === 'string' && amount.trim().startsWith('$')) {
    return amount;
  }
  
  try {
    // Parse amount if it's a string
    const numericAmount = typeof amount === 'string' 
      ? parseFloat(amount.replace(/[^0-9.-]+/g, '')) 
      : amount;
    
    // Format with currency symbol and commas
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numericAmount);
  } catch (error) {
    logger.error('Error formatting currency:', error);
    return '$0';
  }
};
